import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { CableByUsage, SynopticService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

export class CreateCableForm extends FormGroup<{
    cables: FormControl<CableByUsage[]>;
}> {}

export type CreateCableModalData = {
    currentBoxBayId: string;
    currentCables: Set<string>;
    deletedCables: Set<string>;
    depositId: string;
    editSessionId: string;
    isEntryElement: boolean;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComplexModalTemplate, LoadingComponent, RessourcesModule, SelectComponent],
    selector: 'app-create-cable-modal',
    templateUrl: './create-cable-modal.component.html',
})
export class CreateCableModalComponent implements OnInit {
    public data: CreateCableModalData = inject(MAT_DIALOG_DATA);

    protected readonly cables = signal<SelectOption<CableByUsage>[]>([]);
    protected readonly isLoading = signal<boolean>(true);
    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected createCableForm!: CreateCableForm;
    protected dialogRef = inject(MatDialogRef<CreateCableModalComponent>);

    private readonly synopticService = inject(SynopticService);
    private readonly translocoService = inject(TranslocoService);

    public ngOnInit(): void {
        this.initForm();
        void this.initCables();
    }

    public onClickEvent(modalResult: ModalResultEnum): void {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }

        this.dialogRef.close(this.createCableForm.getRawValue().cables);
    }

    public onSelectCablesChange(selectedCables: CableByUsage[]): void {
        this.createCableForm.controls.cables.setValue(selectedCables);
        this.createCableForm.controls.cables.markAsDirty();
        this.createCableForm.controls.cables.markAsTouched();
    }

    private async initCables(): Promise<void> {
        try {
            const cables = await firstValueFrom(
                this.synopticService.synopticControllerGetCablesByUsage({
                    depositId: this.data.depositId,
                    editSessionId: this.data.editSessionId,
                    elementId: this.data.currentBoxBayId,
                    isEntry: this.data.isEntryElement,
                }),
            );
            const deletedCables = Array.from(this.data.deletedCables)
                .filter((key: string) => {
                    return cables.every((cable: CableByUsage) => cable.businessIdentifier !== key);
                })
                .map((key: string) => ({
                    label: key,
                    value: { businessIdentifier: key, isUsed: false },
                }));

            this.cables.set([
                ...deletedCables,
                ...cables.map((cable: CableByUsage): SelectOption<CableByUsage> => {
                    const isCableUsed =
                        (cable.isUsed || this.data.currentCables.has(cable.businessIdentifier)) &&
                        !this.data.deletedCables.has(cable.businessIdentifier);

                    return {
                        label: cable.businessIdentifier,
                        value: cable,
                        ...(isCableUsed && {
                            disabledReason: this.translocoService.translate('synopticPlan.alreadyUsedCable'),
                        }),
                    };
                }),
            ]);
        } catch {
            this.cables.set([]);
        } finally {
            this.isLoading.set(false);
        }
    }

    private initForm(): void {
        this.createCableForm = new CreateCableForm({
            cables: new FormControl<CableByUsage[]>([], {
                nonNullable: true,
                validators: [Validators.required],
            }),
        });
    }
}
